// src/render/sprites/anim-state-machine.ts — pure animation state-resolution core.
//
// AnimStateMachine decides which of seven animation states a character is in,
// enforces D-05 priority (afraid > crouch > sprint > walk > idle), and gates
// non-interruptible catch/free one-shots (D-06).
//
// Pure TypeScript — NO Phaser import. Module has no runtime Phaser dependency
// and is 100% unit-testable without a Phaser context. Mirrors canopy-fade.ts pattern.
//
// Sim/render boundary: module is render-only. Never imported by sim/* files.
// Source: 09-RESEARCH.md Pattern 1; 09-CONTEXT.md D-04/D-05/D-06; 09-UI-SPEC.md
// §Animation State Machine Contract.

// ─── Types ────────────────────────────────────────────────────────────────────

/** All possible character animation states. */
export type AnimState =
  | 'idle'
  | 'walk'
  | 'sprint'
  | 'crouch'
  | 'lunge'
  | 'gesture'
  | 'afraid';

// ─── AnimStateMachine ────────────────────────────────────────────────────────

/**
 * AnimStateMachine — single source of truth for character animation state.
 *
 * Reads boolean/event inputs; produces an AnimState union. Consumed by
 * CharacterSprite (Plan 09-02) to drive either the procedural tween backend
 * or the atlas Sprite backend.
 *
 * Inputs:
 *   update({ isMoving, isSprinting }) — called each frame from CharacterSprite
 *   setAfraid(threshold, direction)  — called on render-bus 'fear-threshold-crossed'
 *   setHiding(hiding)               — called on render-bus 'hiding-entered/exited'
 *   latchOneShot(state)             — called on render-bus 'catch' / 'hider-released'
 *   releaseOneShot()                — called on one-shot animation completion
 */
export class AnimStateMachine {
  private _state: AnimState = 'idle';
  private _latched = false; // true during non-interruptible one-shot (D-06)
  private _afraid = false;
  private _hiding = false;

  /**
   * Called each frame from CharacterSprite with current entity snapshot.
   *
   * Returns the new AnimState if a transition occurred, or null if:
   * - A one-shot is latched (ignored until releaseOneShot() is called), or
   * - The resolved state equals the current state (no transition).
   */
  update(params: { isMoving: boolean; isSprinting: boolean }): AnimState | null {
    if (this._latched) return null; // D-06: ignore all updates while one-shot plays
    const next = this._resolve(params);
    if (next !== this._state) {
      this._state = next;
      return next; // caller applies the state transition
    }
    return null; // no change
  }

  /**
   * D-05 priority resolution: afraid(1) > crouch(2) > sprint(3) > walk(4) > idle(5).
   * Afraid is the emotional beat this game is about — it wins all conflicts.
   */
  private _resolve(params: { isMoving: boolean; isSprinting: boolean }): AnimState {
    if (this._afraid) return 'afraid';
    if (this._hiding) return 'crouch';
    if (params.isSprinting && params.isMoving) return 'sprint';
    if (params.isMoving) return 'walk';
    return 'idle';
  }

  /**
   * Called on render-bus 'fear-threshold-crossed' for this entity.
   *
   * IMPORTANT: the render-bus event payload uses field `threshold` (NOT `name`).
   * Only threshold === 'afraid' triggers the state change; 'nervous' and 'terrified'
   * are deliberately ignored here (D-05: only afraid-level fear overrides movement).
   * Source: 09-RESEARCH.md Pitfall 1; src/render/bus.ts L214-219.
   */
  setAfraid(threshold: string, direction: 'up' | 'down'): void {
    if (threshold === 'afraid') {
      this._afraid = direction === 'up';
    }
    // 'nervous' and 'terrified' are intentionally no-ops in this state machine
  }

  /**
   * Called on render-bus 'hiding-entered' / 'hiding-exited' for this entity.
   * Crouch resolves at D-05 priority 2 (below afraid, above sprint/walk/idle).
   */
  setHiding(hiding: boolean): void {
    this._hiding = hiding;
  }

  /**
   * Called on render-bus 'catch' (state='lunge') or 'hider-released' (state='gesture').
   *
   * Latches the one-shot state and returns its duration in milliseconds:
   *   'lunge'   → 450 ms  (150ms stretch + 300ms return, per UI-SPEC)
   *   'gesture' → 1000 ms (3 cycles × 333ms per cycle, per UI-SPEC)
   *
   * Returns 0 if already latched — D-06: no re-trigger mid-play. Caller treats
   * 0 as a no-op (do not start a new tween / animation).
   */
  latchOneShot(state: 'lunge' | 'gesture'): number {
    if (this._latched) return 0; // D-06: single latch, no re-trigger mid-play
    this._latched = true;
    this._state = state;
    return state === 'lunge' ? 450 : 1000; // UI-SPEC one-shot durations
  }

  /**
   * Called after the one-shot animation completes (tween onComplete or animationcomplete).
   * Clears the latch; the next update() call re-reads current flags and re-resolves
   * to whatever state is now true (idle/walk/afraid/etc.) — D-06.
   */
  releaseOneShot(): void {
    this._latched = false;
  }

  /** Current resolved animation state. */
  get state(): AnimState {
    return this._state;
  }

  /** True while a non-interruptible one-shot (lunge/gesture) is playing. */
  get isLatched(): boolean {
    return this._latched;
  }
}
